// src/components/winback/WinbackConfigForm.jsx
import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSave, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { useWinbackConfig } from '../../hooks/useWinbackConfig';
import Swal from 'sweetalert2';

const WinbackConfigForm = ({ marcaId, onConfigSaved }) => {
    const { config, loading, updateConfig, refreshConfig } = useWinbackConfig(marcaId);
    const [formData, setFormData] = useState(null);
    const [saving, setSaving] = useState(false);
    const [hasChanges, setHasChanges] = useState(false);

    useEffect(() => {
        if (config) {
            // Asegurar que los nuevos campos tengan valores por defecto si no existen
            setFormData({
                ...config,
                cooldownPeriod: config.cooldownPeriod ?? 30,
                incentiveType: config.incentiveType ?? 'light',
                maxRewardPercentage: config.maxRewardPercentage ?? 0.3
            });
        }
    }, [config]);

    const handleToggle = (field) => {
        setFormData(prev => ({
            ...prev,
            [field]: !prev[field]
        }));
        setHasChanges(true);
    };

    const handleThresholdChange = (e) => {
        const value = parseInt(e.target.value) || 30;
        setFormData(prev => ({
            ...prev,
            inactivityThreshold: value
        }));
        setHasChanges(true);
    };

    const handleMultiplierChange = (segment, value) => {
        setFormData(prev => ({
            ...prev,
            segmentMultipliers: {
                ...prev.segmentMultipliers,
                [segment]: parseFloat(value)
            }
        }));
        setHasChanges(true);
    };

    const handleCooldownChange = (e) => {
        const value = parseInt(e.target.value) || 30;
        setFormData(prev => ({
            ...prev,
            cooldownPeriod: value
        }));
        setHasChanges(true);
    };

    const handleIncentiveTypeChange = (e) => {
        setFormData(prev => ({
            ...prev,
            incentiveType: e.target.value
        }));
        setHasChanges(true);
    };

    const handleMaxRewardPercentageChange = (e) => {
        const value = parseFloat(e.target.value) || 0.3;
        setFormData(prev => ({
            ...prev,
            maxRewardPercentage: value
        }));
        setHasChanges(true);
    };

    const handleSave = async () => {
        // Validación
        if (formData.inactivityThreshold < 1 || formData.inactivityThreshold > 365) {
            Swal.fire({
                title: 'Error de Validación',
                text: 'El umbral debe estar entre 1 y 365 días',
                icon: 'error'
            });
            return;
        }

        // Validar cooldownPeriod
        if (formData.cooldownPeriod < 1 || formData.cooldownPeriod > 365) {
            Swal.fire({
                title: 'Error de Validación',
                text: 'El periodo de cooldown debe estar entre 1 y 365 días',
                icon: 'error'
            });
            return;
        }

        // Validar maxRewardPercentage
        if (formData.maxRewardPercentage < 0.05 || formData.maxRewardPercentage > 0.5) {
            Swal.fire({
                title: 'Error de Validación',
                text: 'El porcentaje máximo de recompensa debe estar entre 5% y 50%',
                icon: 'error'
            });
            return;
        }

        setSaving(true);
        try {
            await updateConfig(formData);
            setHasChanges(false);

            // Recargar configuración para asegurar sincronización
            await refreshConfig();

            // Notificar al componente padre para refrescar componentes relacionados
            if (onConfigSaved) {
                onConfigSaved();
            }

            Swal.fire({
                title: '¡Éxito!',
                text: 'Configuración guardada correctamente. Los clientes inactivos y estadísticas se están recargando...',
                icon: 'success',
                confirmButtonText: 'Aceptar',
                timer: 2500,
                showConfirmButton: true
            });
        } catch (error) {
            console.error('Error saving config:', error);
            Swal.fire({
                title: 'Error',
                text: error.message || 'No se pudo guardar la configuración',
                icon: 'error'
            });
        } finally {
            setSaving(false);
        }
    };

    if (loading || !formData) {
        return (
            <div className="flex justify-center items-center h-64">
                <div className="text-center">
                    <FontAwesomeIcon icon={faSpinner} spin className="text-4xl text-purple-600 mb-4" />
                    <p className="text-gray-600">Cargando configuración...</p>
                </div>
            </div>
        );
    }

    const segments = [
        { key: 'Campeones', label: 'Campeones', color: 'from-green-500 to-green-600' },
        { key: 'Clientes Fieles', label: 'Clientes Fieles', color: 'from-blue-500 to-blue-600' },
        { key: 'En Riesgo', label: 'En Riesgo', color: 'from-yellow-500 to-yellow-600' },
        { key: 'No Pueden Perderse', label: 'No Pueden Perderse', color: 'from-orange-500 to-orange-600' },
        { key: 'A Punto de Dormir', label: 'A Punto de Dormir', color: 'from-red-500 to-red-600' },
        { key: 'Perdidos', label: 'Perdidos', color: 'from-gray-500 to-gray-600' },
        { key: 'Sin Compras', label: 'Sin Compras', color: 'from-purple-500 to-purple-600' }
    ];

    return (
        <div className="space-y-6">
            {/* Configuración General */}
            <div className="space-y-4">
                <h4 className="text-sm font-medium text-gray-700 uppercase tracking-wider">
                    Configuración General
                </h4>

                {/* Toggle: Habilitar WinBack */}
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div>
                        <label className="text-sm font-medium text-gray-900">
                            Habilitar WinBack
                        </label>
                        <p className="text-xs text-gray-500 mt-1">
                            Activa el sistema de recuperación de clientes
                        </p>
                    </div>
                    <button
                        onClick={() => handleToggle('enabled')}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${formData.enabled ? 'bg-purple-600' : 'bg-gray-300'
                            }`}
                    >
                        <span
                            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${formData.enabled ? 'translate-x-6' : 'translate-x-1'
                                }`}
                        />
                    </button>
                </div>

                {/* Umbral Base */}
                <div className="p-4 bg-gray-50 rounded-lg">
                    <label className="block text-sm font-medium text-gray-900 mb-2">
                        Umbral Base de Inactividad
                    </label>
                    <div className="flex items-center gap-4">
                        <input
                            type="range"
                            min="1"
                            max="90"
                            value={formData.inactivityThreshold}
                            onChange={handleThresholdChange}
                            className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-purple-600"
                        />
                        <div className="flex items-center gap-2">
                            <input
                                type="number"
                                value={formData.inactivityThreshold}
                                onChange={handleThresholdChange}
                                className="w-20 px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                                min="1"
                                max="365"
                            />
                            <span className="text-sm font-medium text-gray-600">días</span>
                        </div>
                    </div>
                    <p className="text-xs text-gray-500 mt-2">
                        Número de días sin actividad para considerar a un cliente inactivo
                    </p>
                </div>

                {/* Toggle: Sistema Inteligente */}
                <div className="flex items-center justify-between p-4 bg-purple-50 rounded-lg border border-purple-200">
                    <div>
                        <label className="text-sm font-medium text-gray-900 flex items-center">
                            Sistema Inteligente de Umbrales
                            <span className="ml-2 px-2 py-0.5 text-xs font-semibold bg-purple-600 text-white rounded">
                                Recomendado
                            </span>
                        </label>
                        <p className="text-xs text-gray-600 mt-1">
                            Personaliza el umbral para cada cliente según su frecuencia de compra y segmento
                        </p>
                    </div>
                    <button
                        onClick={() => handleToggle('useIntelligentThreshold')}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${formData.useIntelligentThreshold ? 'bg-purple-600' : 'bg-gray-300'
                            }`}
                    >
                        <span
                            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${formData.useIntelligentThreshold ? 'translate-x-6' : 'translate-x-1'
                                }`}
                        />
                    </button>
                </div>

                {/* Toggle: Envío Automático */}
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div>
                        <label className="text-sm font-medium text-gray-900">
                            Envío Automático
                        </label>
                        <p className="text-xs text-gray-500 mt-1">
                            Enviar WinBack automáticamente cada día a las 9:00 AM
                        </p>
                    </div>
                    <button
                        onClick={() => handleToggle('automatic')}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${formData.automatic ? 'bg-purple-600' : 'bg-gray-300'
                            }`}
                    >
                        <span
                            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${formData.automatic ? 'translate-x-6' : 'translate-x-1'
                                }`}
                        />
                    </button>
                </div>

                {/* Periodo de Cooldown */}
                <div className="p-4 bg-gray-50 rounded-lg">
                    <label className="block text-sm font-medium text-gray-900 mb-2">
                        Periodo de Cooldown (días entre envíos)
                    </label>
                    <div className="flex items-center gap-4">
                        <input
                            type="range"
                            min="1"
                            max="365"
                            value={formData.cooldownPeriod || 30}
                            onChange={handleCooldownChange}
                            className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-purple-600"
                        />
                        <div className="flex items-center gap-2">
                            <input
                                type="number"
                                value={formData.cooldownPeriod || 30}
                                onChange={handleCooldownChange}
                                className="w-20 px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                                min="1"
                                max="365"
                            />
                            <span className="text-sm font-medium text-gray-600">días</span>
                        </div>
                    </div>
                    <p className="text-xs text-gray-500 mt-2">
                        Días mínimos entre envíos de WinBack al mismo cliente
                    </p>
                </div>

                {/* Información de Protección Anti-Spam */}
                <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex items-start">
                        <div className="flex-shrink-0">
                            <svg className="h-5 w-5 text-blue-600 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                            </svg>
                        </div>
                        <div className="ml-3">
                            <h4 className="text-sm font-medium text-blue-900">
                                Protección Anti-Spam
                            </h4>
                            <div className="mt-2 text-sm text-blue-800 space-y-1">
                                <p>• No enviaremos más de <strong>1 WinBack cada {formData.cooldownPeriod || 30} días</strong> por cliente</p>
                                <p>• Solo se contactará a clientes que hayan aceptado recibir comunicaciones de marketing</p>
                                <p>• El sistema detecta automáticamente conversiones cuando un cliente vuelve en los 30 días posteriores</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Multiplicadores por Segmento */}
            {formData.useIntelligentThreshold && (
                <div className="space-y-4">
                    <div className="flex items-center justify-between">
                        <h4 className="text-sm font-medium text-gray-700 uppercase tracking-wider">
                            Multiplicadores por Segmento RFM
                        </h4>
                        <span className="text-xs text-gray-500">
                            Ajusta el umbral según el segmento del cliente
                        </span>
                    </div>

                    <div className="space-y-3">
                        {segments.map((segment) => {
                            const multiplier = formData.segmentMultipliers[segment.key] || 1.0;
                            const adjustedThreshold = Math.round(formData.inactivityThreshold * multiplier);

                            return (
                                <div key={segment.key} className="p-4 bg-gray-50 rounded-lg">
                                    <div className="flex items-center justify-between mb-3">
                                        <div className="flex items-center gap-2">
                                            <div className={`w-3 h-3 rounded-full bg-gradient-to-r ${segment.color}`}></div>
                                            <span className="text-sm font-medium text-gray-900">
                                                {segment.label}
                                            </span>
                                        </div>
                                        <div className="flex items-center gap-3">
                                            <span className="text-xs text-gray-500">
                                                Umbral: {adjustedThreshold} días
                                            </span>
                                            <span className="text-sm font-bold text-purple-600">
                                                x{multiplier.toFixed(1)}
                                            </span>
                                        </div>
                                    </div>
                                    <input
                                        type="range"
                                        min="0.1"
                                        max="3.0"
                                        step="0.1"
                                        value={multiplier}
                                        onChange={(e) => handleMultiplierChange(segment.key, e.target.value)}
                                        className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-purple-600"
                                    />
                                    <div className="flex justify-between text-xs text-gray-400 mt-1">
                                        <span>0.1x (Más urgente)</span>
                                        <span>1.0x (Base)</span>
                                        <span>3.0x (Menos urgente)</span>
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                        <p className="text-xs text-blue-800">
                            <strong>Tip:</strong> Valores menores a 1.0 hacen que el sistema envíe WinBack más rápido.
                            Valores mayores a 1.0 esperan más tiempo antes de enviar.
                        </p>
                    </div>
                </div>
            )}

            {/* Configuración de Incentivos WinBack */}
            <div className="space-y-4">
                <div className="flex items-center justify-between">
                    <h4 className="text-sm font-medium text-gray-700 uppercase tracking-wider">
                        Sistema de Incentivos WinBack
                    </h4>
                    <span className="text-xs text-gray-500">
                        Protege tu estrategia de fidelización
                    </span>
                </div>

                {/* Tipo de Incentivo */}
                <div className="p-4 bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-200 rounded-lg">
                    <label className="block text-sm font-medium text-gray-900 mb-2">
                        Tipo de Incentivo WinBack
                    </label>
                    <select
                        value={formData.incentiveType || 'light'}
                        onChange={handleIncentiveTypeChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white"
                    >
                        <option value="light">Ligero (5-10% del premio, inmediato) - Recomendado</option>
                        <option value="conditioned">Condicionado (hasta 30%, se activa después)</option>
                        <option value="deferred">Diferido (hasta 30%, siguiente visita) - Máxima protección</option>
                    </select>
                    <div className="mt-2 text-xs text-gray-600 space-y-1">
                        <p><strong>Ligero:</strong> Ideal para alta recurrencia (peluquerías, bares, cafeterías)</p>
                        <p><strong>Condicionado:</strong> Evita abusos, se activa después de la visita</p>
                        <p><strong>Diferido:</strong> Máxima protección, se aplica en la siguiente visita</p>
                    </div>
                </div>

                {/* Porcentaje Máximo de Recompensa */}
                <div className="p-4 bg-gray-50 rounded-lg">
                    <label className="block text-sm font-medium text-gray-900 mb-2">
                        Porcentaje Máximo de Recompensa: {((formData.maxRewardPercentage || 0.3) * 100).toFixed(0)}%
                    </label>
                    <div className="flex items-center gap-4">
                        <input
                            type="range"
                            min="0.05"
                            max="0.5"
                            step="0.05"
                            value={formData.maxRewardPercentage || 0.3}
                            onChange={handleMaxRewardPercentageChange}
                            className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-purple-600"
                        />
                        <div className="flex items-center gap-2">
                            <input
                                type="number"
                                value={((formData.maxRewardPercentage || 0.3) * 100).toFixed(0)}
                                onChange={(e) => {
                                    const value = parseFloat(e.target.value) / 100;
                                    if (value >= 0.05 && value <= 0.5) {
                                        handleMaxRewardPercentageChange({ target: { value: value.toString() } });
                                    }
                                }}
                                className="w-20 px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                                min="5"
                                max="50"
                            />
                            <span className="text-sm font-medium text-gray-600">%</span>
                        </div>
                    </div>
                    <p className="text-xs text-gray-500 mt-2">
                        Recomendado: 10%-30%. Nunca más del 50% para proteger la fidelización.
                    </p>
                    <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                        <p className="text-xs text-yellow-800">
                            <strong>Regla del 30%:</strong> Un WinBack nunca otorga más del porcentaje configurado del valor del premio final. Esto protege tu estrategia de fidelización evitando comportamientos oportunistas.
                        </p>
                    </div>
                </div>
            </div>

            {/* Botón Guardar */}
            <div className="pt-4 border-t border-gray-200">
                <button
                    onClick={handleSave}
                    disabled={saving || !hasChanges}
                    className={`w-full flex items-center justify-center px-4 py-3 border border-transparent text-sm font-medium rounded-lg text-white transition-all ${saving || !hasChanges
                        ? 'bg-gray-400 cursor-not-allowed'
                        : 'bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 shadow-lg hover:shadow-xl'
                        }`}
                >
                    {saving ? (
                        <>
                            <FontAwesomeIcon icon={faSpinner} spin className="mr-2" />
                            Guardando...
                        </>
                    ) : (
                        <>
                            <FontAwesomeIcon icon={faSave} className="mr-2" />
                            {hasChanges ? 'Guardar Cambios' : 'Sin Cambios'}
                        </>
                    )}
                </button>
            </div>
        </div>
    );
};

export default WinbackConfigForm;

